import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

export type FavoriteEntry = { text: string; at: string; name?: string }
export type FavoriteState = {
  favorites: FavoriteEntry[]
  favoriteSet: Set<string>
  loaded: boolean
  isFavorite: (text: string) => boolean
  addFavorite: (text: string) => void
  removeFavorite: (text: string) => void
  toggleFavorite: (text: string) => void
  updateFavorite: (originalText: string, text: string, name?: string) => void
}

export function useFavorites(): FavoriteState {
  const client = useClient()
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([])
  const [loaded, setLoaded] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    void (async () => {
      try {
        const res = await client.assistantFavoriteGet({})
        if (!isMountedRef.current) return
        setFavorites(sortFavorites(res))
      } catch (err) {
        logError('assistant', 'failed to load favorites', err)
      } finally {
        if (isMountedRef.current) setLoaded(true)
      }
    })()
    return () => {
      isMountedRef.current = false
    }
  }, [client])

  const addFavorite = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const at = new Date().toISOString()
      setFavorites((prev) => {
        const filtered = prev.filter((e) => e.text !== trimmed)
        return sortFavorites([{ text: trimmed, at }, ...filtered])
      })
      void (async () => {
        try {
          const res = await client.assistantFavoriteAdd({ text: trimmed })
          if (!isMountedRef.current) return
          setFavorites(sortFavorites(res))
        } catch (err) {
          logError('assistant', 'failed to add favorite', err)
        }
      })()
    },
    [client],
  )

  const removeFavorite = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const previous = favorites
      setFavorites((prev) => prev.filter((e) => e.text !== trimmed))
      void (async () => {
        try {
          const res = await client.assistantFavoriteDelete({ text: trimmed })
          if (!isMountedRef.current) return
          setFavorites(sortFavorites(res))
        } catch (err) {
          if (isMountedRef.current) setFavorites(previous)
          logError('assistant', 'failed to remove favorite', err)
        }
      })()
    },
    [client, favorites],
  )

  const updateFavorite = useCallback(
    (originalText: string, text: string, name?: string) => {
      const trimmedOriginal = originalText.trim()
      const trimmedNew = text.trim()
      if (!trimmedOriginal || !trimmedNew) return
      // PATCH name semantics (matches server):
      //   - name omitted (undefined): preserve existing label
      //   - explicit empty/whitespace string: clear the label
      //   - non-empty string: set as the new label after trim
      const previous = favorites
      setFavorites((prev) => {
        const existing = prev.find((e) => e.text === trimmedOriginal)
        if (!existing) return prev
        const resolvedName =
          name === undefined ? existing.name : name.trim() ? name.trim() : undefined
        const updated: FavoriteEntry = {
          text: trimmedNew,
          at: existing.at,
          ...(resolvedName !== undefined ? { name: resolvedName } : {}),
        }
        const filtered = prev.filter((e) => e.text !== trimmedOriginal && e.text !== trimmedNew)
        return sortFavorites([updated, ...filtered])
      })
      void (async () => {
        try {
          const res = await client.assistantFavoriteUpdate({
            originalText: trimmedOriginal,
            text: trimmedNew,
            // Forward `name` verbatim when present so the server sees the
            // caller's intent — including '' which means "clear". Omitting
            // the field means "preserve current".
            ...(name !== undefined ? { name } : {}),
          })
          if (!isMountedRef.current) return
          setFavorites(sortFavorites(res))
        } catch (err) {
          if (isMountedRef.current) setFavorites(previous)
          logError('assistant', 'failed to update favorite', err)
        }
      })()
    },
    [client, favorites],
  )

  const favoriteSet = useMemo(() => new Set(favorites.map((e) => e.text)), [favorites])
  const isFavorite = useCallback((text: string) => favoriteSet.has(text.trim()), [favoriteSet])

  const toggleFavorite = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      if (favoriteSet.has(trimmed)) {
        removeFavorite(trimmed)
      } else {
        addFavorite(trimmed)
      }
    },
    [addFavorite, favoriteSet, removeFavorite],
  )

  return {
    favorites,
    favoriteSet,
    loaded,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    updateFavorite,
  }
}

function sortFavorites(favorites: FavoriteEntry[]): FavoriteEntry[] {
  return [...favorites].sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
}
